export function normalizeTireSize(input) {
  const source = String(input || '').trim().toUpperCase().replace(/[×✕]/g, 'X').replace(/^(?:LT|ST|P)\s*(?=\d)/, '');
  let text = source.replace(/\s/g, '');
  if (/^\d{7}$/.test(text)) text = `${text.slice(0, 3)}/${text.slice(3, 5)}R${text.slice(5)}`;
  const metric = source.match(/^(\d{3})\s*[\/-]?\s*(\d{2})\s*(?:Z?R|-)\s*(\d{2})(?=\D|$)/) || text.match(/^(\d{3})[\/-]?(\d{2})(?:Z?R|-)(\d{2})$/);
  if (metric) return `${metric[1]}/${metric[2]}R${metric[3]}`;
  const flotation = text.match(/^(\d{2}(?:\.\d+)?)X(\d{1,2}(?:\.\d+)?)(?:R|-)(\d{2})/);
  if (flotation) return `${Number(flotation[1])}X${Number(flotation[2])}-${flotation[3]}`;
  const agricultural = text.match(/^(\d{1,2}(?:\.\d+)?)(?:R|-)(\d{2}(?:\.\d+)?)/);
  if (agricultural) return `${Number(agricultural[1])}R${Number(agricultural[2])}`;
  return text;
}

export function normalizeTireSearchQuery(input) {
  const original = String(input || '').trim();
  const expanded = original.match(/^"(\d{3}\/\d{2}R\d{2})" OR "LT\1" OR "ST\1" OR "P\1"$/i);
  if (expanded) return expanded[1].toUpperCase();
  const compact = original.replace(/^"(.*)"$/, '$1').toUpperCase().replace(/\s/g, '').replace(/[×✕]/g, 'X');
  if (/^(?:LT|ST|P)?(?:\d{7}|\d{3}[\/-]?\d{2}Z?R\d{2}|\d{3}[\/-]\d{2}-\d{2})$/.test(compact)) {
    const canonical = normalizeTireSize(compact);
    const dimensions = canonical.match(/^(\d{3})\/(\d{2})R(\d{2})$/);
    if (dimensions && Number(dimensions[1]) >= 100 && Number(dimensions[1]) <= 455 && Number(dimensions[2]) >= 20 && Number(dimensions[2]) <= 95 && Number(dimensions[3]) >= 10 && Number(dimensions[3]) <= 30) return canonical;
  }
  if (/^\d{2}(?:\.\d+)?X\d{1,2}(?:\.\d+)?(?:R|-)\d{2}$/.test(compact)) return compact;
  return original;
}

export function tireSearchSize(input) {
  const normalized = normalizeTireSearchQuery(input);
  return /^(?:\d{3}\/\d{2}R\d{2}|\d{2}(?:\.\d+)?X\d{1,2}(?:\.\d+)?(?:R|-)\d{2})$/.test(normalized) ? normalizeTireSize(normalized) : null;
}

export function tireSearchPhrase(input) {
  const size = tireSearchSize(input);
  if (!size) return String(input || '').trim();
  return /^\d{3}\/\d{2}R\d{2}$/.test(size)
    ? ['', 'LT', 'ST', 'P'].map(prefix => `"${prefix}${size}"`).join(' OR ')
    : `"${size}"`;
}

export function productSearchDestination(path, input, origin = 'https://apextiresupplyco.com') {
  const url = new URL(path, origin);
  url.searchParams.set('type', 'product');
  url.searchParams.set('q', tireSearchPhrase(input));
  if (tireSearchSize(input)) url.searchParams.set('options[prefix]', 'none');
  return url;
}

export function exactTireSearchDestination(path, input, data, origin = 'https://apextiresupplyco.com') {
  const url = productSearchDestination(path, input, origin);
  const size = tireSearchSize(input);
  if (!size) return url;
  const filters = (data?.filters || []).filter(filter => filter.type === 'list' && (
    filter.name === 'filter.v.option.size' || /^filter\.[pv]\.m\..*\.(?:tire_size|tiresize|size)$/.test(filter.name) || String(filter.label).toLowerCase() === 'tire size'
  )).sort((a, b) => Number(b.name.includes('.m.')) - Number(a.name.includes('.m.')));
  for (const filter of filters) {
    const values = [...new Set((filter.values || []).filter(value => normalizeTireSize(value.value) === size).map(value => String(value.value)))];
    if (!values.length) continue;
    url.searchParams.set('q', size);
    url.searchParams.delete(filter.name);
    for (const value of values) url.searchParams.append(filter.name, value);
    break;
  }
  return url;
}

export async function resolveTireSearchDestination(path, input, origin = 'https://apextiresupplyco.com', fetcher = fetch) {
  const fallback = productSearchDestination(path, input, origin);
  const size = tireSearchSize(input);
  if (!size) return fallback;
  const dataURL = new URL(fallback);
  dataURL.searchParams.set('q', size);
  dataURL.searchParams.set('section_id', 'apex-finder-data');
  dataURL.searchParams.delete('page');
  for (const key of [...dataURL.searchParams.keys()]) if (key.startsWith('filter.')) dataURL.searchParams.delete(key);
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 6000);
  try {
    const response = await fetcher(dataURL, { credentials: 'same-origin', signal: controller.signal });
    if (!response.ok) return fallback;
    const node = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-apex-filter-data]');
    if (!node) return fallback;
    return exactTireSearchDestination(path, size, JSON.parse(node.textContent), origin);
  } catch {
    return fallback;
  } finally {
    clearTimeout(timeout);
  }
}

export function identifyFilters(data) {
  const map = {};
  for (const filter of data?.filters || []) {
    if (filter.type !== 'list') continue;
    const name = String(filter.name || '').toLowerCase();
    const label = String(filter.label || '').toLowerCase();
    if (/tire_width/.test(name) || /^(tire )?width$/.test(label)) map.width = filter;
    else if (/aspect_ratio/.test(name) || /aspect ratio/.test(label)) map.aspect = filter;
    else if (/wheel_diameter/.test(name) || /wheel diameter|rim diameter/.test(label)) map.rim = filter;
    else if (name === 'filter.p.vendor' || /^(tire )?brand$/.test(label)) map.brand = filter;
    else if (name === 'filter.v.option.size' || label === 'tire size') map.size = filter;
  }
  return map;
}

export function groupedOptions(filter, key, selected = '', includeUnavailable = false) {
  const groups = new Map();
  for (const item of filter?.values || []) {
    const raw = String(item.value);
    const label = ['width', 'aspect', 'rim'].includes(key) && /^\d+(?:\.\d+)?$/.test(raw) ? String(Number(raw)) : String(item.label || raw);
    if (label === 'Default Title' || label === 'Standard') continue;
    if (!includeUnavailable && !item.count && !item.active && label !== selected) continue;
    if (!groups.has(label)) groups.set(label, { label, values: [], count: 0 });
    const group = groups.get(label);
    if (!group.values.includes(raw)) group.values.push(raw);
    group.count += Number(item.count || 0);
  }
  return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
}

export function filterDestination(path, map, state, origin = 'https://apextiresupplyco.com') {
  const url = new URL(path, origin);
  for (const key of ['width', 'aspect', 'rim', 'brand']) {
    if (!state[key] || !map[key]) continue;
    const option = groupedOptions(map[key], key, state[key], true).find(item => item.label === state[key]);
    if (option) for (const value of option.values) url.searchParams.append(map[key].name, value);
  }
  return url;
}

export function sizeDestination(path, map, input, origin = 'https://apextiresupplyco.com') {
  const canonical = normalizeTireSize(input);
  const values = (map.size?.values || []).filter(item => normalizeTireSize(item.value) === canonical);
  if (!canonical || !values.length) return null;
  const url = new URL(path, origin);
  for (const value of values) url.searchParams.append(map.size.name, value.value);
  return url;
}

if (typeof document !== 'undefined') {
  window.ApexSearch = { normalize: normalizeTireSearchQuery, predictiveQuery: tireSearchPhrase };
  function normalizeSearchInput(input) {
    if (!(input instanceof HTMLInputElement) || input.type === 'hidden' || input.matches('[data-apex-direct]')) return;
    const appInput = input.matches('#search-input, .t4s-mini-search__input, .tmenu_search_input, #ts7StickyModalSearchInput');
    if (input.name !== 'q' && !appInput) return;
    const size = tireSearchSize(input.value);
    if (!size) return;
    input.value = size;
    if (typeof window.ts7_search_value === 'string') window.ts7_search_value = size;
  }
  document.addEventListener('input', event => normalizeSearchInput(event.target), true);
  document.addEventListener('submit', event => {
    if (!(event.target instanceof HTMLFormElement)) return;
    for (const input of event.target.querySelectorAll('input[name="q"]')) normalizeSearchInput(input);
  }, true);
  const pendingSearches = new WeakMap();
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (event.defaultPrevented || !(form instanceof HTMLFormElement) || form.matches('[data-apex-direct-form]')) return;
    const query = form.querySelector('input[type="search"][name="q"]');
    if (!query || !new URL(form.action, location.origin).pathname.endsWith('/search')) return;
    const size = tireSearchSize(query.value);
    if (!size) return;
    event.preventDefault();
    query.value = size;
    const token = {};
    pendingSearches.set(form, token);
    form.setAttribute('aria-busy', 'true');
    const path = new URL(form.action, location.origin);
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) path.searchParams.set('preview_theme_id', preview);
    const result = await resolveTireSearchDestination(path, size, location.origin);
    if (pendingSearches.get(form) !== token) return;
    pendingSearches.delete(form);
    form.removeAttribute('aria-busy');
    if (tireSearchSize(query.value) === size) location.assign(result.href);
  });

  class ApexTireFinder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.useControl = this.querySelector('[data-apex-use]');
      this.strings = JSON.parse(this.querySelector('[data-apex-strings]').textContent);
      this.data = JSON.parse(this.querySelector('[data-apex-initial]').textContent);
      this.map = identifyFilters(this.data);
      this.baseMap = this.map;
      this.state = { width: '', aspect: '', rim: '', brand: '' };
      this.fields = Object.fromEntries([...this.querySelectorAll('[data-apex-field]')].map(field => [field.dataset.apexField, field]));
      this.status = this.querySelector('[data-apex-status]');
      const current = new URL(location.href);
      this.preview = current.searchParams.get('preview_theme_id');
      if (current.pathname === new URL(this.collectionPath(), location.origin).pathname) {
        for (const [key, filter] of Object.entries(this.map)) {
          if (!(key in this.state)) continue;
          const raw = current.searchParams.get(filter.name);
          const option = groupedOptions(filter, key, '', true).find(item => item.values.includes(raw));
          if (option) this.state[key] = option.label;
        }
      }
      this.renderOptions();
      this.useControl.addEventListener('change', () => {
        this.state = { width: '', aspect: '', rim: '', brand: '' };
        this.map = {};
        this.baseMap = {};
        this.querySelector('[data-apex-direct]').value = '';
        this.renderOptions();
        this.loadFilters(true);
      });
      for (const [key, field] of Object.entries(this.fields)) field.addEventListener('change', () => {
        this.state[key] = field.value;
        if (key === 'width') this.state.aspect = this.state.rim = '';
        if (key === 'aspect') this.state.rim = '';
        this.renderOptions();
        this.loadFilters();
      });
      this.querySelector('[data-apex-size-form]').addEventListener('submit', event => {
        event.preventDefault();
        this.go(filterDestination(this.collectionPath(), this.map, this.state, location.origin));
      });
      this.querySelector('[data-apex-brand-form]').addEventListener('submit', event => {
        event.preventDefault();
        this.go(filterDestination(this.collectionPath(), this.map, this.state, location.origin));
      });
      this.querySelector('[data-apex-direct-form]').addEventListener('submit', event => {
        event.preventDefault();
        const input = this.querySelector('[data-apex-direct]');
        if (!input.value.trim()) { input.setCustomValidity(this.strings.enter_size); input.reportValidity(); return; }
        input.setCustomValidity('');
        const result = sizeDestination(this.collectionPath(), this.map, input.value, location.origin) || productSearchDestination(this.dataset.searchUrl, input.value, location.origin);
        const brand = filterDestination(this.collectionPath(), this.map, { brand: this.state.brand }, location.origin);
        for (const [key, value] of brand.searchParams) result.searchParams.append(key, value);
        this.go(result);
      });
      this.querySelector('[data-apex-direct]').addEventListener('input', event => {
        event.target.setCustomValidity('');
        this.showSearchHint();
      });
      this.querySelector('[data-apex-vehicle-form]').addEventListener('submit', event => {
        event.preventDefault();
        const values = new FormData(event.currentTarget);
        const vehicle = ['finder_year', 'finder_make', 'finder_model'].map(key => values.get(key)?.trim()).filter(Boolean).join(' ');
        this.go(this.quoteDestination({ finder_vehicle: vehicle }));
      });
      this.querySelector('[data-apex-quote-link]').addEventListener('click', event => {
        event.preventDefault();
        const size = this.querySelector('[data-apex-direct]').value.trim() || (this.state.width && this.state.aspect && this.state.rim ? `${this.state.width}/${this.state.aspect}R${this.state.rim}` : '');
        this.go(this.quoteDestination({ finder_size: size }));
      });
      const tabs = [...this.querySelectorAll('[data-apex-tab]')];
      const selectTab = tab => {
        for (const item of tabs) { item.setAttribute('aria-selected', String(item === tab)); item.tabIndex = item === tab ? 0 : -1; }
        for (const panel of this.querySelectorAll('[data-apex-panel]')) panel.hidden = panel.dataset.apexPanel !== tab.dataset.apexTab;
      };
      for (const tab of tabs) {
        tab.addEventListener('click', () => selectTab(tab));
        tab.addEventListener('keydown', event => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let next = tabs.indexOf(tab) + (event.key === 'ArrowRight' ? 1 : -1);
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          const target = tabs[(next + tabs.length) % tabs.length];
          selectTab(target); target.focus();
        });
      }
      this.loadFilters(!Object.keys(this.map).length);
    }

    disconnectedCallback() { this.controller?.abort(); }

    collectionPath() { return this.useControl.selectedOptions[0].dataset.url; }

    quoteDestination(extra = {}) {
      const url = new URL(this.dataset.quoteUrl, location.origin);
      const params = { finder_use: this.useControl.selectedOptions[0].textContent.trim(), finder_brand: this.state.brand, ...extra };
      for (const [key, value] of Object.entries(params)) if (value) url.searchParams.set(key, value);
      return url;
    }

    go(url) {
      if (this.preview) url.searchParams.set('preview_theme_id', this.preview);
      location.assign(url.href);
    }

    setLoading(loading) {
      this.loading = loading;
      this.setAttribute('aria-busy', String(loading));
      for (const button of this.querySelectorAll('[data-apex-size-form] button, [data-apex-brand-form] button')) button.disabled = loading;
      if (loading) this.status.textContent = this.strings.loading;
      this.showSearchHint();
    }

    showSearchHint() {
      const value = this.querySelector('[data-apex-direct]').value.trim();
      if (value && this.strings.search_hint) this.status.textContent = this.strings.search_hint.replace('__SIZE__', normalizeTireSearchQuery(value));
    }

    renderOptions() {
      const dimensions = !!(this.map.width && this.map.aspect && this.map.rim);
      this.querySelector('[data-apex-dimensions]').hidden = !dimensions;
      for (const [key, field] of Object.entries(this.fields)) {
        const map = key === 'width' && this.baseMap.width ? this.baseMap : this.map;
        const options = groupedOptions(map[key], key, this.state[key]);
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = key === 'aspect' && !this.state.width ? this.strings.select_width : key === 'rim' && !this.state.aspect ? this.strings.select_aspect : this.strings.any;
        field.replaceChildren(placeholder, ...options.map(option => {
          const node = document.createElement('option'); node.value = option.label; node.textContent = option.label; return node;
        }));
        if (this.state[key] && !options.some(option => option.label === this.state[key])) this.state[key] = '';
        field.value = this.state[key];
        field.disabled = !options.length || (key === 'aspect' && !this.state.width) || (key === 'rim' && !this.state.aspect);
        field.closest('form').action = this.collectionPath();
      }
    }

    async loadFilters(setBase = false) {
      this.controller?.abort();
      const controller = this.controller = new AbortController();
      const url = filterDestination(this.collectionPath(), this.map, this.state, location.origin);
      url.searchParams.set('section_id', 'apex-finder-data');
      if (this.preview) url.searchParams.set('preview_theme_id', this.preview);
      this.setLoading(true);
      const timeout = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Catalog unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const node = html.querySelector('[data-apex-filter-data]');
        if (!node) throw new Error('Catalog filters unavailable');
        const data = JSON.parse(node.textContent);
        if (controller !== this.controller) return;
        this.data = data;
        this.map = identifyFilters(data);
        if (setBase) this.baseMap = this.map;
        this.renderOptions();
        this.status.textContent = Object.keys(this.map).length ? this.strings.ready : this.strings.fallback;
      } catch (error) {
        if (controller !== this.controller) return;
        this.status.textContent = this.strings.fallback;
      } finally {
        clearTimeout(timeout);
        if (controller === this.controller) this.setLoading(false);
      }
    }
  }
  if (!customElements.get('apex-tire-finder')) customElements.define('apex-tire-finder', ApexTireFinder);

  function prefillQuote() {
    const params = new URLSearchParams(location.search);
    if (!params.has('finder_use') || !location.pathname.endsWith('/pages/request-a-quote')) return;
    const pairs = { finder_size: 'tire_wheel_size', finder_vehicle: 'year_make_model', finder_brand: 'preferred_brand' };
    for (const [parameter, name] of Object.entries(pairs)) {
      const input = document.querySelector(`[name="${name}"]`);
      if (input && !input.value && params.get(parameter)) input.value = params.get(parameter).slice(0, 160);
    }
    const notes = document.querySelector('[name="notes"]');
    if (notes && !notes.value) notes.value = [params.get('finder_use').slice(0, 80), params.get('finder_query')?.slice(0, 160)].filter(Boolean).join('\n');
  }
  function initializeSearchPage() {
    prefillQuote();
    const current = new URL(location.href);
    const query = current.searchParams.get('q') || '';
    const size = tireSearchSize(query);
    if (current.pathname.endsWith('/apps/search-result') && size) {
      for (const input of document.querySelectorAll('input[name="q"], #ts7StickyModalSearchInput')) normalizeSearchInput(input);
      if (typeof window.ts7_search_value === 'string') window.ts7_search_value = size;
      if (query !== size) {
        current.searchParams.set('q', size);
        current.searchParams.delete('page');
        location.replace(current.href);
      }
      return;
    }
    if (!current.pathname.endsWith('/search') || !size) return;
    for (const input of document.querySelectorAll('input[type="search"][name="q"]')) input.value = size;
    const exact = [...current.searchParams.keys()].some(key => key === 'filter.v.option.size' || /^filter\.[pv]\.m\..*\.(?:tire_size|tiresize|size)$/.test(key));
    if (exact || query === tireSearchPhrase(size)) {
      current.searchParams.set('q', exact ? size : tireSearchPhrase(size));
      current.searchParams.set('type', 'product');
      current.searchParams.set('options[prefix]', 'none');
      if (current.href !== location.href) { current.searchParams.delete('page'); location.replace(current.href); }
    } else {
      current.searchParams.delete('page');
      resolveTireSearchDestination(current, size, location.origin).then(result => {
        if (result.href !== location.href) location.replace(result.href);
      });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeSearchPage, { once: true });
  else initializeSearchPage();
}
